import { useCallback, useEffect, useMemo, useRef } from 'react';
import { InlineNotification } from '@carbon/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ChartLine } from '@carbon/react/icons';
import {
  chartViewResetAtom,
  eligibleWellsInViewAtom,
  hoveredDistanceFtAtom,
  refineBaselineAtom,
  refineViewportAtom,
  cropToGridExtentsAtom,
  depthReferenceAtom,
  distanceUnitAtom,
  gridColorRulesAtom,
  hostCrossingsAtom,
  hostWellColorsAtom,
  wellColorRulesAtom,
  sectionDatumGridIdAtom,
  sectionFlippedAtom,
  sectionGridIdsAtom,
  sectionModeAtom,
  sectionVerticesAtom,
  sectionWellsAtom,
  faultResultsRequestedAtom,
  focusedWellUwiAtom,
  showFaultResultsAtom,
  showGroundSurfaceAtom,
  showIntervalFillsAtom,
  showLabelsAtom,
  showLocationIndicatorAtom,
  axisZoomEnabledAtom,
  showWellResultsAtom,
  wellResultsRequestedAtom,
  structureGridCollectionIdAtom,
  knownGridsAtom,
} from './atoms';
import { defaultGridColor, resolveGridColors } from './grid-colors';
import { resolveDefiningWellColors, resolveWellColors } from './well-colors';
import { useSectionWells } from './use-section-wells';
import { useStructureGridCollection } from './use-structure-grid-collections';
import {
  depthAxisTitle,
  invertDepthAxis,
  type ProfileSeries,
  paddedTraverseRange,
} from './profile-plan';
import { CrossSectionProfileChart } from './cross-section-profile-chart';
import { useCrossSection } from './use-cross-section';
import { useCrossSectionRequest } from './use-cross-section-request';
import {
  datumDepthsFor,
  toGroundProfile,
  toProfileCrossings,
  refineWindow,
  toProfileSticks,
  toProfileFaults,
  toProfileSeries,
  toProfileVertices,
  type DepthHanging,
} from './utils';
import { format0 } from 'utils/number';
import styles from './cross-section-body.module.scss';

/**
 * A crossing marker, until the section reads the Map View's own colour-by rule for
 * wells. Navy, which is what the map draws a well trace in.
 */
const CROSSING_COLOR = '#003a6d';

/** A fault whose own display colour is not set. */
const FAULT_COLOR = '#e8611d';

/**
 * What the drawer says while there is no plot, in the words of the gesture that
 * would produce one.
 *
 * It names the gesture and nothing else. The drawer only reaches this state with the
 * tool already armed in a mode the user has just picked, so telling them to use the
 * cross-section tool would describe a step that is behind them.
 *
 * Both modes count progress once there is any: one point placed and no line drawn is
 * the state most likely to read as broken. Past two the picks are in and the wait is
 * the sampling, so the count would answer a question nobody is asking any more.
 *
 * @param {object} options - The mode and how much of the plane exists.
 * @return {string} The message to show.
 */
const instructionFor = ({
  mode,
  count,
}: {
  mode: 'arbitrary_line' | 'well_to_well';
  count: number;
}): string => {
  if (count >= 2) {
    return 'Loading Cross Section…';
  }
  if (mode === 'well_to_well') {
    return count > 0
      ? `Click wells on the map to pick them — ${count} picked so far, two or more make a section.`
      : 'Click wells on the map to pick them. Two or more make a section.';
  }
  return count > 0
    ? `Click points on the map to draw the section — ${count} placed so far, two or more make a section.`
    : 'Click points on the map to draw the section. Two or more make a section, and vertices can be dragged afterwards.';
};

/**
 * The empty, loading and failed states, drawn where the plot would be.
 *
 * @param {object} props - The message to stand in for the plot.
 * @return {JSX.Element} The placeholder.
 */
const Placeholder = ({ message }: { message: string }): JSX.Element => (
  <div className={styles.placeholder}>
    <ChartLine size={28} />
    <p>{message}</p>
  </div>
);

/**
 * The drawer's body: the section in profile, or whatever stands in for it.
 *
 * The order of the states is the order the questions arise — is there a plane, is
 * there a stack, did the cut come back, does the cut have anything along this line.
 *
 * A failed cut keeps the drawn line. The line is the user's work and the failure is
 * ours; clearing it would make a server error look like an undo.
 *
 * @return {JSX.Element} The profile, or a placeholder.
 */
export const CrossSectionBody = (): JSX.Element => {
  const mode = useAtomValue(sectionModeAtom);
  const vertices = useAtomValue(sectionVerticesAtom);
  const wells = useAtomValue(sectionWellsAtom);
  const gridIds = useAtomValue(sectionGridIdsAtom);
  const datumGridId = useAtomValue(sectionDatumGridIdAtom);
  const depthReference = useAtomValue(depthReferenceAtom);
  const distanceUnit = useAtomValue(distanceUnitAtom);
  const flipped = useAtomValue(sectionFlippedAtom);
  const showGroundPreference = useAtomValue(showGroundSurfaceAtom);
  const showLabels = useAtomValue(showLabelsAtom);
  const showFills = useAtomValue(showIntervalFillsAtom);
  const cropPreference = useAtomValue(cropToGridExtentsAtom);
  const resetSignal = useAtomValue(chartViewResetAtom);
  const wellsRequested = useAtomValue(wellResultsRequestedAtom);
  const faultsRequested = useAtomValue(faultResultsRequestedAtom);
  const showWells = useAtomValue(showWellResultsAtom);
  const showFaults = useAtomValue(showFaultResultsAtom);
  const focusedWellUwi = useAtomValue(focusedWellUwiAtom);
  const eligibleInView = useAtomValue(eligibleWellsInViewAtom);
  const showIndicator = useAtomValue(showLocationIndicatorAtom);
  const axisZoom = useAtomValue(axisZoomEnabledAtom);
  // A plot click SETS the selection (or clears it with null) rather than toggling:
  // the card's × and a click on open plot are the ways out, like the mock.
  const setFocusedWellUwi = useSetAtom(focusedWellUwiAtom);
  const [hoveredDistanceFt, setHoveredDistanceFt] = useAtom(
    hoveredDistanceFtAtom
  );
  const collectionId = useAtomValue(structureGridCollectionIdAtom);
  const colorRules = useAtomValue(gridColorRulesAtom);
  const wellColorRules = useAtomValue(wellColorRulesAtom);
  const hostCrossings = useAtomValue(hostCrossingsAtom);
  const hostWellColors = useAtomValue(hostWellColorsAtom);

  // Well to Well always draws the ground: every bore hangs from ground level to total
  // depth, so without it the sticks hang from nothing. The Arbitrary Line preference is
  // read but not written, so it survives the round trip.
  const showGround = mode === 'well_to_well' ? true : showGroundPreference;
  // Held separately from the mode, so switching to Well to Well and back does not
  // lose what the user set in Arbitrary Line.
  const cropped = mode === 'arbitrary_line' && cropPreference;

  const { request, generation } = useCrossSectionRequest();
  const { data, grouped, error } = useCrossSection(request, generation);
  const filteredWells = useSectionWells(grouped.wells);
  // The host's own crossings ride beside the cut's, past the rail's filters: they are
  // the sheet's unsaved edit, which no filter was written against.
  const crossingWells = useMemo(() => {
    if (hostCrossings.length === 0) return filteredWells;
    return [...filteredWells, ...hostCrossings];
  }, [filteredWells, hostCrossings]);
  // The same cached read the rail makes, for the intervals' stored display colours.
  const { grids: collectionGrids } = useStructureGridCollection(collectionId);
  const knownGrids = useAtomValue(knownGridsAtom);

  const [refineBaseline, setRefineBaseline] = useAtom(refineBaselineAtom);
  const setRefineViewport = useSetAtom(refineViewportAtom);

  // Read inside the axis callback, which the chart registers once with its surface.
  const baselineRef = useRef(refineBaseline);
  baselineRef.current = refineBaseline;

  // What the whole line reported about how finely it can be sampled, taken only from a
  // cut that covered the whole line. A refined cut reports the step it was handed, so
  // asking it whether to refine would flip the request between the two for ever.
  useEffect(() => {
    if (!data || data.subject.viewport !== null) {
      return;
    }
    const next = {
      lengthFt: data.subject.lengthFt,
      stepFt: data.subject.effectiveStepFt,
      nativeResolutionFt: data.subject.nativeResolutionFt,
    };
    setRefineBaseline((current) => {
      if (
        current &&
        current.lengthFt === next.lengthFt &&
        current.stepFt === next.stepFt &&
        current.nativeResolutionFt === next.nativeResolutionFt
      ) {
        return current;
      }
      return next;
    });
  }, [data, setRefineBaseline]);

  // The plot's own zoom, turned into the window worth asking for. The request settles
  // before it goes out, so a zoom gesture asks once at the end rather than per frame.
  const onVisibleRangeChange = useCallback(
    (range: [number, number]) => {
      setRefineViewport((current) => {
        const next = refineWindow(range, baselineRef.current);
        if (
          current?.startFt === next?.startFt &&
          current?.endFt === next?.endFt
        ) {
          return current;
        }
        return next;
      });
    },
    [setRefineViewport]
  );

  // The crossings the rail's filters leave, which are the ones the rail counts and
  // colours — the plot draws exactly what the rail describes. Named apart from the
  // picked wells above: those are what defines the line, these are what it cuts.

  // Hanging on a datum is a re-reference of depths already held, not a re-cut — so it
  // is resolved here, over the response, and never reaches the network. The datum wins
  // over the depth reference: relative depths are not TVD or TVDSS any more.
  const hanging = useMemo<DepthHanging>(() => {
    if (datumGridId !== null) {
      return { kind: 'flattened', datumGridId: String(datumGridId) };
    }
    return { kind: depthReference };
  }, [datumGridId, depthReference]);

  const series = useMemo<ProfileSeries[]>(() => {
    // The rail's rule decides the colours, over the metadata the response carries per
    // grid, so plot and rail cannot disagree. The interval's stored colour rides along
    // for grids a narrowed rule leaves out, rather than a meaningless palette default.
    const colorByGridId = resolveGridColors(
      grouped.horizons.map((horizon) => ({
        gridId: horizon.gridId,
        geologicInterval: horizon.geologicInterval,
        gridName: horizon.gridName,
        dataSource: horizon.dataSource,
        dataOwner: horizon.dataOwner,
        displayColor:
          (
            collectionGrids.find(
              (grid) => String(grid.structureGridId) === horizon.gridId
            ) ?? knownGrids[horizon.gridId]
          )?.geologicIntervalDisplayColor ?? null,
      })),
      colorRules
    );
    return toProfileSeries(grouped.horizons, hanging, (gridId) => {
      return colorByGridId.get(gridId) ?? defaultGridColor(0);
    });
  }, [grouped.horizons, hanging, colorRules, collectionGrids, knownGrids]);

  const ground = useMemo(
    () => toGroundProfile(grouped.groundSurface, hanging),
    [grouped.groundSurface, hanging]
  );

  // Hiding is a glance and recalculating is a round trip, so the switch decides only
  // whether the latest available answer is drawn.
  const crossings = useMemo(() => {
    if (!wellsRequested || !showWells) {
      return [];
    }
    const datum = datumDepthsFor(grouped.horizons, hanging);
    // The rail's Wells rule owns what a well colour means, so the plot and the rail
    // cannot disagree. With no rule every crossing is the same navy: a per-well
    // colour with nothing behind it would be a meaning the section invented.
    const colorByUwi = resolveWellColors(crossingWells, wellColorRules);
    // A host that colours its own wells (the geometry sheet, as its map does) is
    // read first; it sets nothing where the rail's rules are the meaning.
    return toProfileCrossings(crossingWells, hanging, datum, (well) => {
      return (
        hostWellColors[well.uwi] ?? colorByUwi.get(well.uwi) ?? CROSSING_COLOR
      );
    });
  }, [
    wellsRequested,
    showWells,
    crossingWells,
    grouped.horizons,
    hanging,
    wellColorRules,
    hostWellColors,
  ]);

  const faults = useMemo(() => {
    if (!faultsRequested || !showFaults) {
      return [];
    }
    return toProfileFaults(grouped.faults, FAULT_COLOR);
  }, [faultsRequested, showFaults, grouped.faults]);

  // The wells the plane was built from, as bores at their own vertices. They come with
  // the plane rather than the crossings — a well cannot cross a plane it defines — so
  // they draw with any Well to Well section, without waiting on the crossings switch.
  const sticks = useMemo(() => {
    const vertexLabels = data?.subject.vertexLabels;
    if (!vertexLabels || vertexLabels.length === 0) {
      return [];
    }
    const datum = datumDepthsFor(grouped.horizons, hanging);
    const colorByUwi = resolveDefiningWellColors(vertexLabels, wellColorRules);
    const nameByUwi = new Map(
      wells
        .filter((well) => well.name !== null)
        .map((well) => [well.uwi, well.name as string])
    );
    return toProfileSticks(
      vertexLabels,
      hanging,
      datum,
      (uwi) => colorByUwi.get(uwi) ?? CROSSING_COLOR,
      (uwi) => nameByUwi.get(uwi) ?? null
    );
  }, [
    data?.subject.vertexLabels,
    grouped.horizons,
    hanging,
    wellColorRules,
    wells,
  ]);

  // What the section holds at the position both views point at. Depths come from the
  // cut rather than from the plot, so they stay true under a flattened section.

  // County lines are context rather than a calculated result, so they need no switch:
  // they are asked for with every cut and drawn whenever the cut carries them.
  const boundaries = useMemo(
    () =>
      grouped.boundaries.map((boundary, index) => ({
        id: boundary.id ?? `county-${index}`,
        name: boundary.name,
        state: boundary.state,
        xFt: boundary.xFt,
        // Which way the line steps across this boundary, which is what decides the
        // side the county lies on.
        entering: boundary.entering,
      })),
    [grouped.boundaries]
  );

  // Both of these are in the chart's `renderDeps`, so they are memoised rather than
  // built inline: a fresh array on every render re-stages the whole SciChart surface,
  // and the hovered distance changes on every pointer move across the map.
  const chartVertices = useMemo(() => {
    if (!data) {
      return [];
    }
    // In Well to Well a guide per well, without a letter: the letter would stand in
    // for the wellbore, and the wellbore itself is drawn here now.
    return mode === 'well_to_well'
      ? data.subject.vertexLabels.map((vertex) => ({
          distanceFt: vertex.xFt,
          label: null,
        }))
      : toProfileVertices(data.subject);
  }, [data, mode]);

  // Pinned to the drawn line, keeping the corner labels on the section's real endpoints.
  // Cropping closes the axis in on where grid data exists — Arbitrary Line only, since in
  // Well to Well cropping could cut off the very wells being sectioned.
  const chartXRange = useMemo<[number, number] | null>(() => {
    if (cropped || !data) {
      return null;
    }
    const { a, aPrime } = data.subject.endpoints;
    // The frame is widened in Well to Well so the first and last labels sit whole and
    // centred over their bores.
    return mode === 'well_to_well'
      ? paddedTraverseRange(a.xFt, aPrime.xFt)
      : [a.xFt, aPrime.xFt];
  }, [cropped, data, mode]);

  // Picks the backend refused — empty in the ordinary case, since the picker only offers
  // vertical wells. Named by the pick's own label, because a bare UWI does not tell
  // anyone which well went missing.
  const excluded = useMemo(() => {
    const uwis = data?.subject.excludedUwis ?? [];
    if (uwis.length === 0) {
      return [];
    }
    return uwis.map((uwi) => {
      const pick = wells.find((well) => well.uwi === uwi);
      return pick?.name ?? uwi;
    });
  }, [data?.subject.excludedUwis, wells]);

  if (mode === null) {
    // The drawer's own placeholder covers this; nothing to plot without a mode.
    return <Placeholder message="Choose how to build the section." />;
  }

  const count = mode === 'arbitrary_line' ? vertices.length : wells.length;

  if (error) {
    return <Placeholder message={error.message} />;
  }

  // Telling someone to click wells where there are none to click reads as a broken
  // tool. Only before any pick: once wells are on the section, panning away from them
  // is not a problem to report.
  if (mode === 'well_to_well' && count === 0 && eligibleInView === 0) {
    return (
      <Placeholder message="No vertical wells in this view. Pan or zoom to where they are — only vertical wells can define a section." />
    );
  }

  // Placeholder only while there is nothing to draw: once a plot exists it stays mounted
  // through every re-cut. That is the keepPreviousData rule, and what keeps a zoom alive
  // — unmounting for the settle aborted the refine it had just asked for.
  if (count < 2 || !data) {
    return <Placeholder message={instructionFor({ mode, count })} />;
  }

  // Grids are one layer of the section, not its licence to draw: with none selected the
  // plot still draws the ground, the bores, the crossings and the faults. Only a section
  // with none of those falls back to a message saying what would put something on it.
  const hasValues = series.some(
    (line) => line.points.filter((point) => point.depthFt !== null).length >= 2
  );
  const hasOtherContent =
    (showGround &&
      (ground?.filter((point) => point.depthFt !== null).length ?? 0) >= 2) ||
    sticks.length > 0 ||
    crossings.length > 0 ||
    faults.length > 0;
  if (!hasValues && !hasOtherContent) {
    return (
      <Placeholder
        message={
          gridIds.length === 0
            ? 'No grids selected. Add one, pick a different grid collection, or calculate well intersections to plot this section.'
            : 'No grid values along this line. Pick a different grid collection, or calculate well intersections to plot this section.'
        }
      />
    );
  }

  const datumInterval = datumGridId
    ? (grouped.horizons.find(
        (horizon) => horizon.gridId === String(datumGridId)
      )?.geologicInterval ?? null)
    : null;

  return (
    <div className={styles.plotted}>
      {excluded.length > 0 && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          className={styles.notice}
          title={
            excluded.length === 1
              ? 'One pick is not eligible'
              : `${format0(excluded.length)} picks are not eligible`
          }
          subtitle={`Left out of this section: ${excluded.join(
            ', '
          )}. Only vertical wells can define one.`}
        />
      )}
      <div className={styles.plot}>
        <CrossSectionProfileChart
          series={series}
          ground={ground}
          showGround={showGround}
          vertices={chartVertices}
          guideEnds={mode === 'well_to_well'}
          xRange={chartXRange}
          depthAxisTitle={depthAxisTitle({ depthReference, datumInterval })}
          invertDepthAxis={invertDepthAxis({ depthReference, datumInterval })}
          // Hung on a datum, the county rules and fault bands are left full height:
          // depths are read against the datum there, so no value on the axis is the
          // ground to stop them at.
          flattened={hanging.kind === 'flattened'}
          distanceUnit={distanceUnit}
          flipped={flipped}
          showLabels={showLabels}
          showFills={showFills}
          resetSignal={resetSignal}
          axisZoom={axisZoom}
          crossings={crossings}
          faults={faults}
          sticks={sticks}
          boundaries={boundaries}
          focusedWellUwi={focusedWellUwi}
          hoveredDistanceFt={showIndicator ? hoveredDistanceFt : null}
          onHoverDistanceChange={
            showIndicator ? setHoveredDistanceFt : undefined
          }
          onWellPick={setFocusedWellUwi}
          onVisibleRangeChange={onVisibleRangeChange}
        />
      </div>
    </div>
  );
};
